import { AxiosPromise } from 'axios';
import { http } from 'services';
import type { CompanySize } from 'modules/auth/types';
import type { OrgOnboardingProfile, OrgUseCase } from 'modules/auth/onboarding';

// Organization CRUD
export const GetAll = (): AxiosPromise =>
  http.request.get('/api/organizations');

export const GetById = (id: string): AxiosPromise =>
  http.request.get(`/api/organizations/${id}`);

export const Create = (data: { name: string; slug?: string }): AxiosPromise =>
  http.request.post('/api/organizations', data);

export const Update = (
  id: string,
  data: {
    name?: string;
    slug?: string;
    logoUrl?: string;
    settings?: Record<string, unknown>;
    companySize?: CompanySize;
    useCase?: OrgUseCase;
    /** Replaces the stored profile; send every answer, an empty object clears it. */
    onboardingProfile?: Omit<OrgOnboardingProfile, 'updatedAt'>;
  }
): AxiosPromise =>
  http.request.patch(`/api/organizations/${id}`, data);

export const UploadLogo = (id: string, file: File): AxiosPromise<{ logoUrl: string }> => {
  const form = new FormData();
  form.append('file', file);
  return http.request.post(`/api/organizations/${id}/logo`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const Delete = (id: string, confirmationName: string): AxiosPromise =>
  http.request.delete(`/api/organizations/${id}`, { data: { confirmationName } });

export const TransferOwnership = (id: string, targetUserId: string): AxiosPromise<{ organizationId: string; ownerId: string }> =>
  http.request.post(`/api/organizations/${id}/ownership-transfer`, { targetUserId });

// Members
export const GetMembers = (id: string, params?: Record<string, string | number | undefined>): AxiosPromise => {
  const searchParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        searchParams.set(key, String(value));
      }
    });
  }
  const query = searchParams.toString();
  return http.request.get(`/api/organizations/${id}/members${query ? `?${query}` : ''}`);
};

export const GetMember = (orgId: string, memberId: string): AxiosPromise =>
  http.request.get(`/api/organizations/${orgId}/members/${memberId}`);

export const AddMember = (id: string, data: { email: string; role: string }): AxiosPromise =>
  http.request.post(`/api/organizations/${id}/members`, data);

export const UpdateMemberRole = (orgId: string, memberId: string, data: { role: string }): AxiosPromise =>
  http.request.patch(`/api/organizations/${orgId}/members/${memberId}`, data);

export const UpdateMemberStatus = (orgId: string, memberId: string, data: { status: string }): AxiosPromise =>
  http.request.patch(`/api/organizations/${orgId}/members/${memberId}/status`, data);

export const RemoveMember = (orgId: string, memberId: string): AxiosPromise =>
  http.request.delete(`/api/organizations/${orgId}/members/${memberId}`);

// Organization Invites
export const CreateOrgInvite = (
  orgId: string,
  data: { role: string; expiresAt?: string; maxUses?: number }
): AxiosPromise =>
  http.request.post(`/api/organizations/${orgId}/invites`, data);

export const GetOrgInvites = (orgId: string): AxiosPromise =>
  http.request.get(`/api/organizations/${orgId}/invites`);

export const DeleteOrgInvite = (orgId: string, inviteId: string): AxiosPromise =>
  http.request.delete(`/api/organizations/${orgId}/invites/${inviteId}`);

export const ValidateOrgInvite = (code: string): AxiosPromise =>
  http.request.get(`/api/organizations/invites/${encodeURIComponent(code)}/validate`);

export const JoinByInvite = (code: string): AxiosPromise =>
  http.request.post(`/api/organizations/join/${code}`);

// Email Invitations
export const SendEmailInvite = (
  orgId: string,
  data: { email: string; role: string }
): AxiosPromise =>
  http.request.post(`/api/organizations/${orgId}/email-invites`, data);

export const ValidateInvitation = (token: string): AxiosPromise =>
  http.request.get(`/api/organizations/invitations/${token}/validate`);

export const AcceptInvitation = (token: string): AxiosPromise =>
  http.request.post(`/api/organizations/invitations/${token}/accept`);

export const DeclineInvitation = (token: string): AxiosPromise =>
  http.request.post(`/api/organizations/invitations/${token}/decline`);
